import PropTypes from 'prop-types';
import React from 'react';
import classNames from 'classnames/bind';
import Icon from 'yoda-core-components/lib/components/Icon/Icon';
import LockWhite from 'yoda-core-components/lib/assets/svg/LockWhite.svg';
import Button from 'yoda-core-components/lib/components/Button';
import ModalBox from 'yoda-core-components/lib/components/ModalBox/ModalBox';
import NavigationHelper from 'yoda-core-components/lib/helpers/NavigationHelper/NavigationHelper';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import CertonaZone from '../CertonaZone/CertonaZone';
import * as styles from './CouponAppliedModal.css';
import {
    CART_YODA_EXPERIENCE,
    CLASSIC_CART_PAGE_URL,
    CART_PAGE_URL,
    CONTINUE_SHOPPING_LABEL,
    OK_LABEL,
    IRIS_CONTINUE_SHOPPING_LABEL,
    CHECKOUT_LABEL,
    COUPONS_APPLIED_LABEL,
    COUPON_APPLIED_SHOP_NOW_LABEL,
    RECOMMENDATION_SOURCE_TYPE_ADOBE,
} from '../../common/Constants';
import Config from '../CouponCard/CouponConfig';

const cx = classNames.bind(styles);

/* istanbul ignore next */
const handleContinueCheckout = () => {
    window.location.href = CART_YODA_EXPERIENCE === 'OFF' ? CLASSIC_CART_PAGE_URL : CART_PAGE_URL;
};

// errorType  1: Empty cart warnings, 0: All error messages
function CouponAppliedModal({
    title,
    savings,
    message,
    onClose,
    channel,
    level,
    shopSalesLink,
    errorType,
    applyingToEmptyCart,
    applyingToEmptyCartCTA,
    deviceType,
    promoCode,
}) {
    const svgName = Config.svgNameArray[level].icon;
    const svgIconComponent = Config.svgNameArray[level].iconComponent;
    /* istanbul ignore next */
    const showMsg =
        message &&
        (message.includes(Config.COUPON_QUAL_ORIG_MSG) ? Config.COUPON_QUAL_TRANS_MSG : message);

    let couponAppliedHtml = (
        <Button
            type="button"
            automationId="continue-shopping"
            buttonType="Secondary"
            size="Lg"
            className={`${cx('continueShoppingBtn')} ${dt(['mx-auto', 'w-60'])}`}
            onClick={onClose}
        >
            {CONTINUE_SHOPPING_LABEL}
        </Button>
    );

    const onClickCouponAppliedEmptyCartCTA = (cta) => {
        onClose();
        window.location.assign(cta);
    };

    const couponAppliedEmptyCartHtml = (
        <Button
            type="button"
            automationId="coupon-modal-shop-now"
            buttonType="Primary"
            size="Lg"
            onClick={() => onClickCouponAppliedEmptyCartCTA(applyingToEmptyCartCTA)}
            className={`${cx('iconBtn')} ${dt(['mx-auto', 'w-60'])}`}
        >
            {COUPON_APPLIED_SHOP_NOW_LABEL}
        </Button>
    );

    const recommendationsForEmptyCart = () => (
        <CertonaZone
            page="COUPON"
            slotDetails={{
                pageType: 'COUPON',
                attributes: {
                    'entity.couponId': `${promoCode}`,
                    recommendations: true,
                    schemes: 'coupons3_jrecs',
                },
                loader: RECOMMENDATION_SOURCE_TYPE_ADOBE,
                slotId: 'coupons3_jrecs',
            }}
            schemasToRender="coupons3_jrecs"
            forceRefresh
            isTitle={false}
            isCouponRecommendationsModal
        />
    );

    if (errorType === 1) {
        const shopHtml = [];
        if (shopSalesLink) {
            shopHtml.push(
                <div>
                    <Button
                        type="button"
                        automationId="continue-shopping"
                        buttonType="Primary"
                        size="Lg"
                        className={`${cx('okBtn')} ${dt([
                            'font-open-sans-bold',
                            'text-normal',
                            'inline-block',
                            'w-60',
                            'm-2',
                            'smOnly:w-full',
                            'smOnly:mx-0',
                            'smOnly:mt-0',
                            'smOnly:mb-1',
                        ])}`}
                        onClick={onClose}
                    >
                        {OK_LABEL}
                    </Button>
                    <Button
                        type="button"
                        automationId="continue-shopping"
                        buttonType="Secondary"
                        size="Lg"
                        className={`${cx('continueShoppingBtn', 'salesLink')} ${dt([
                            'text-gray-70',
                            'text-normal',
                            'font-open-sans-bold',
                            'inline-block',
                            'w-60',
                            'm-2',
                            'float-left',
                            'smOnly:w-full',
                            'mt-1',
                            'mx-0',
                            'mb-0',
                        ])}`}
                        onClick={
                            /* istanbul ignore next */
                            () => {
                                NavigationHelper.navigate(shopSalesLink);
                            }
                        }
                    >
                        {IRIS_CONTINUE_SHOPPING_LABEL}
                    </Button>
                </div>
            );
        } else {
            shopHtml.push(
                <Button
                    type="button"
                    automationId="continue-shopping"
                    buttonType="Primary"
                    size="Lg"
                    className={`${cx('okBtn')} ${dt([
                        'font-open-sans-bold',
                        'text-normal',
                        'inline-block',
                        'w-60',
                        'm-2',
                        'smOnly:w-full',
                        'smOnly:mx-0',
                        'smOnly:mt-0',
                        'smOnly:mb-1',
                    ])}`}
                    onClick={onClose}
                >
                    {OK_LABEL}
                </Button>
            );
        }
        couponAppliedHtml = shopHtml;
    }

    return (
        <ModalBox
            showModal
            renderModalToBody
            onClose={onClose}
            defaultHeader
            automationId="at-cpn-applied-modal"
            modalTheme={`${cx('appliedModalTheme', { emptyCart: applyingToEmptyCart })} ${dt([
                '!px-3',
                '!pt-3',
                '!pb-8',
                'md:w-[470px]',
                'lg:w-[644px]',
                'smOnly:mt-0',
            ])}`}
            modalContentTheme={`${cx('appliedModalContentTheme')} ${dt([
                'text-center',
                'smOnly:mt-0',
            ])}`}
            modalOverlayTheme={`${cx('appliedModalOverlayTheme')} ${dt(['z-[101]'])}`}
            deviceType={deviceType}
        >
            <div className={`${cx('coupon', 'couponApplied', 'alignCenter')} ${dt(['mt-2.5'])}`}>
                <div className={`${cx('couponStatusIcon')} ${dt(['p-0'])}`}>
                    <Icon
                        iconType="svg"
                        automationId="coupon-applied"
                        width="96px"
                        height="96px"
                        viewBox="0 0 96 96"
                        pathClassName={styles.iconPath}
                    >
                        {svgIconComponent}
                    </Icon>
                </div>
                <div
                    className={`${cx('couponStatus')} ${dt([
                        'text-2xlarge',
                        'font-open-sans-bold',
                        'text-black',
                    ])}`}
                >
                    {title}
                </div>
                {savings ? (
                    <div
                        data-automation-id="at-saved-amt-msg"
                        className={`${cx('savedPriceInfo')} ${dt([
                            'text-normal',
                            'mb-4',
                            'text-success-border',
                        ])}`}
                    >
                        You Saved ${parseFloat(savings).toFixed(2)}
                    </div>
                ) : (
                    ''
                )}
                <div
                    data-automation-id="at-coupon-msg"
                    className={`${cx('couponMessage')} ${dt([
                        'text-gray-70',
                        'text-small',
                        'font-sans',
                        'mb-5',
                    ])}`}
                    dangerouslySetInnerHTML={{ __html: showMsg }} // eslint-disable-line react/no-danger
                />

                {channel === 'TOF' && applyingToEmptyCart ? (
                    <div
                        className={`${cx('couponRecommendedItemsContainer')} ${dt([
                            'mb-10',
                            'overflow-hidden',
                            'hidden',
                            'smMd:mb-7',
                        ])}`}
                    >
                        <div
                            className={cx('couponRecommendedItemsWrapper', {
                                desktopWrapper: deviceType.isDesktop,
                            })}
                        >
                            {recommendationsForEmptyCart()}
                        </div>
                    </div>
                ) : null}

                {channel === 'TOF' && savings ? (
                    <div className={`${cx('couponCheckoutCta')} ${dt(['mb-2'])}`}>
                        <Button
                            type="button"
                            automationId="coupon-checkout"
                            buttonType="Primary"
                            size="Lg"
                            onClick={handleContinueCheckout}
                            className={`${'iconBtn'} ${dt([
                                'font-open-sans-bold',
                                'my-0',
                                'mx-auto',
                                'max-h-12',
                                'w-60',
                            ])}`}
                        >
                            <Icon
                                iconType="svg"
                                automationId="test-automation-icon"
                                width="32px"
                                height="32px"
                                viewBox="0 0 32 32"
                                className="btnIcon"
                            >
                                <LockWhite />
                            </Icon>
                            {CHECKOUT_LABEL}
                        </Button>
                    </div>
                ) : null}

                <div className={cx('continueShoppingCta')}>
                    {channel === 'BOF' ? (
                        <Button
                            type="button"
                            automationId="continue-shopping"
                            buttonType="Primary"
                            size="Lg"
                            className={dt([
                                'font-open-sans-bold',
                                'my-0',
                                'mx-auto',
                                'max-h-12',
                                'w-60',
                            ])}
                            onClick={onClose}
                        >
                            {OK_LABEL}
                        </Button>
                    ) : (
                        <div>
                            {applyingToEmptyCart ? couponAppliedEmptyCartHtml : couponAppliedHtml}
                        </div>
                    )}
                </div>
            </div>
        </ModalBox>
    );
}

CouponAppliedModal.defaultProps = {
    currency: 'USD',
    message: '',
    title: COUPONS_APPLIED_LABEL,
    channel: 'TOF',
    level: 0,
    shopSalesLink: '',
    savings: 0,
    errorType: 0,
    applyingToEmptyCart: false,
    applyingToEmptyCartCTA: '',
    promoCode: '',
};

CouponAppliedModal.propTypes = {
    title: PropTypes.string,
    savings: PropTypes.number,
    message: PropTypes.string,
    onClose: PropTypes.func.isRequired,
    channel: PropTypes.string,
    level: PropTypes.number,
    shopSalesLink: PropTypes.string,
    errorType: PropTypes.number,
    applyingToEmptyCart: PropTypes.bool,
    applyingToEmptyCartCTA: PropTypes.string,
    deviceType: PropTypes.object.isRequired,
    promoCode: PropTypes.string,
};

export default CouponAppliedModal;
